import {
  Box,
  CircularProgress,
  ModalActionButton,
  Radio,
  RadioGroup,
  RadioTypography,
  Stack,
  TextField,
  useMultilineTextValidator,
} from '@ace-clublabs/shared-component-library';
import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  buildCancelAppointmentPayload,
  getCancellationReasonIndex,
  getCancellationReasonRadioTabIndex,
  getFocusTargetAfterRadiogroupTab,
  getNextCancellationReasonIndex,
  getRadioLabelFontWeight,
  SCROLL_RETRY_DELAYS_MS,
  scheduleDelayedCallbacks,
  scrollIntoModalView,
} from '../utils/cancelAppointmentModal';
import {
  CANCELLATION_REASON_OTHER_COMMENT_MAX_LENGTH,
  isCancelAppointmentPayloadValid,
} from '../utils/cancellationReasonLimits';
import {
  CANCELLATION_REASON_OTHER,
  type CancellationReason,
  cancellationReasons,
} from '../utils/cancellationReasons';

interface CancelAppointmentModalBodyProps {
  cancellationReasonPromptId: string;
  isModalOpen?: boolean;
  onClickCancel: (cancellationReason: string) => void;
  onClickKeepAppointment: () => void;
  isCancellingAppointment: boolean;
}

const otherCommentInputStyles = {
  '& .MuiInputBase-root': {
    backgroundColor: 'common.white',
    minHeight: '90px',
    overflow: 'hidden',
  },
  '& .MuiTextField-root': {
    textAlign: 'left',
  },
  '& .MuiInputBase-inputMultiline': {
    border: '0 !important',
  },
};

const cancellationReasonGroupSx = {
  width: '100%',
  m: 0,
  gap: 3,
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiFormGroup-root': {
    gap: 3,
    width: '100%',
  },
  '& .MuiRadio-FormControlLabel-root': {
    width: '100%',
    margin: 0,
    alignItems: 'flex-start',
  },
  '& .MuiRadio-FormControlLabel-Variant-outlined': {
    borderRadius: '8px',
    px: '10px',
    py: '12px',
    borderColor: 'grey.100',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  '& .MuiRadio-root.MuiRadioVariant-outlined': {
    marginRight: '0.75rem',
  },
  '& .MuiRadio-LabelStack': {
    textAlign: 'left',
    alignItems: 'flex-start',
  },
  '& .MuiRadio-FormControlLabel-Variant-outlined:has(input:checked)': {
    borderColor: 'primary.600',
    boxShadow: (theme: { palette: { primary: { 600: string } } }) =>
      `inset 0 0 0 1px ${theme.palette.primary[600]}`,
  },
  '& .MuiRadio-FormControlLabel-Variant-outlined:has(input:focus-visible)': {
    outline: (theme: { palette: { primary: { 300: string } } }) =>
      `3px solid ${theme.palette.primary[300]}`,
    outlineOffset: '2px',
  },
};

function CancelAppointmentModalBody({
  cancellationReasonPromptId,
  isModalOpen = true,
  onClickCancel,
  onClickKeepAppointment,
  isCancellingAppointment,
}: CancelAppointmentModalBodyProps) {
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason | null>(null);
  const [focusedReasonIndex, setFocusedReasonIndex] = useState(0);
  const [otherComment, setOtherComment] = useState('');
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const modalActionsRef = useRef<HTMLDivElement>(null);
  const radioInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const otherCommentCounterId = useId();
  const otherCommentValidator = useMultilineTextValidator({
    required: false,
    maxLength: CANCELLATION_REASON_OTHER_COMMENT_MAX_LENGTH,
    showCharacterCount: true,
  });

  const queryWithinBody = <T extends HTMLElement>(selector: string): T | null =>
    modalBodyRef.current?.querySelector(selector) as T | null;

  const focusCancellationReasonRadio = (index: number) => {
    radioInputRefs.current[index]?.focus();
  };

  const focusCloseButton = () => {
    modalBodyRef.current
      ?.closest('[role="dialog"]')
      ?.querySelector<HTMLButtonElement>('button[aria-label="Close"]')
      ?.focus();
  };

  const focusKeepButton = () => {
    queryWithinBody<HTMLElement>(
      '[data-testid="keepAppointmentButton"]',
    )?.focus();
  };

  const focusOtherCommentField = () => {
    queryWithinBody<HTMLTextAreaElement>(
      '[data-testid="cancellationOtherComment"] textarea',
    )?.focus();
  };

  const focusCancelOrKeepButton = () => {
    const cancelButton = queryWithinBody<HTMLElement>(
      '[data-testid="confirmCancelAppointmentButton"]',
    );

    if (cancelButton?.getAttribute('aria-disabled') !== 'true') {
      cancelButton?.focus();
      return;
    }

    focusKeepButton();
  };

  const focusAfterRadiogroupTab = () => {
    const nextTarget = getFocusTargetAfterRadiogroupTab(selectedReason);

    if (nextTarget === 'otherComment') {
      focusOtherCommentField();
      return;
    }

    focusCancelOrKeepButton();
  };

  const handleReasonChange = (event: ChangeEvent<HTMLInputElement>) => {
    const reason = event.target.value as CancellationReason;
    setSelectedReason(reason);

    const selectedIndex = cancellationReasons.indexOf(reason);
    if (selectedIndex >= 0) {
      setFocusedReasonIndex(selectedIndex);
    }
  };

  const handleOtherCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherComment(
      event.target.value.slice(0, CANCELLATION_REASON_OTHER_COMMENT_MAX_LENGTH),
    );
  };

  const handleOtherCommentKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') {
      return;
    }

    event.preventDefault();

    if (event.shiftKey) {
      focusCancellationReasonRadio(
        getCancellationReasonIndex(selectedReason, focusedReasonIndex),
      );
      return;
    }

    focusCancelOrKeepButton();
  };

  const handleRadiogroupKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();

      if (event.shiftKey) {
        focusCloseButton();
        return;
      }

      focusAfterRadiogroupTab();
      return;
    }

    const nextIndex = getNextCancellationReasonIndex(
      event.key,
      selectedReason,
      focusedReasonIndex,
      cancellationReasons.length,
    );
    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    setFocusedReasonIndex(nextIndex);
    setSelectedReason(cancellationReasons[nextIndex]);
    focusCancellationReasonRadio(nextIndex);
  };

  const isCancelDisabled =
    !isCancelAppointmentPayloadValid(selectedReason, otherComment) ||
    isCancellingAppointment;

  useEffect(() => {
    if (isModalOpen) return;

    setSelectedReason(null);
    setFocusedReasonIndex(0);
    setOtherComment('');
  }, [isModalOpen]);

  useEffect(() => {
    if (selectedReason !== CANCELLATION_REASON_OTHER) return;

    return scheduleDelayedCallbacks(() => {
      const element = modalActionsRef.current;
      if (element) scrollIntoModalView(element);
    }, SCROLL_RETRY_DELAYS_MS);
  }, [selectedReason]);

  const handleCancelAppointment = () => {
    const cancellationReason = buildCancelAppointmentPayload(
      selectedReason,
      otherComment,
    );
    if (!cancellationReason) return;

    onClickCancel(cancellationReason);
  };

  return (
    <Box
      ref={modalBodyRef}
      sx={{
        px: 3,
        pb: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        textAlign: 'left',
        width: '100%',
      }}
    >
      <RadioGroup
        variant='outlined'
        fullWidth
        aria-labelledby={cancellationReasonPromptId}
        value={selectedReason ?? ''}
        onChange={handleReasonChange}
        onKeyDown={handleRadiogroupKeyDown}
        data-testid='cancellationReasonGroup'
        sx={cancellationReasonGroupSx}
      >
        {cancellationReasons.map((reason, index) => (
          <Radio
            key={reason}
            value={reason}
            inputRef={(node) => {
              radioInputRefs.current[index] = node;
            }}
            inputProps={{
              tabIndex: getCancellationReasonRadioTabIndex(
                index,
                selectedReason,
                focusedReasonIndex,
              ),
              onFocus: () => setFocusedReasonIndex(index),
            }}
            label={
              <RadioTypography
                variant='regular'
                sx={{
                  color: 'grey.800',
                  fontSize: '1rem',
                  lineHeight: '1.5rem',
                  fontWeight: getRadioLabelFontWeight(
                    selectedReason === reason,
                  ),
                }}
              >
                {reason}
              </RadioTypography>
            }
            data-testid={`cancellationReason-${index}`}
          />
        ))}
      </RadioGroup>

      {selectedReason === CANCELLATION_REASON_OTHER && (
        <Box onKeyDown={handleOtherCommentKeyDown}>
          <TextField
            name='cancellationOtherComment'
            multiline
            fullWidth
            required={false}
            placeholder='Tell us more (optional)'
            value={otherComment}
            onChange={handleOtherCommentChange}
            validator={otherCommentValidator}
            data-testid='cancellationOtherComment'
            slotProps={{
              htmlInput: {
                'aria-label': 'Tell us more (optional)',
                'aria-describedby': otherCommentCounterId,
                maxLength: CANCELLATION_REASON_OTHER_COMMENT_MAX_LENGTH,
              },
              formHelperText: {
                id: otherCommentCounterId,
              },
            }}
            sx={otherCommentInputStyles}
          />
        </Box>
      )}

      <Stack ref={modalActionsRef} gap={3} sx={{ width: '100%' }}>
        <ModalActionButton
          onClick={handleCancelAppointment}
          label='Cancel appointment'
          fullWidth
          disabled={isCancelDisabled}
          tabIndex={isCancelDisabled ? -1 : 0}
          data-testid='confirmCancelAppointmentButton'
          endIcon={
            isCancellingAppointment ? (
              <CircularProgress
                size={20}
                aria-hidden
                data-testid='circularProgress'
              />
            ) : null
          }
          sx={{
            borderRadius: '6px',
            py: '12px',
            px: '16px',
            '& .MuiTypography-root': {
              fontSize: '1rem',
              lineHeight: '1.5rem',
              fontWeight: 500,
            },
          }}
        />
        <ModalActionButton
          label='Keep Appointment'
          onClick={onClickKeepAppointment}
          variant='outlined'
          fullWidth
          data-testid='keepAppointmentButton'
          sx={{
            borderRadius: '6px',
            py: '12px',
            px: '16px',
            '& .MuiTypography-root': {
              fontSize: '1rem',
              lineHeight: '1.5rem',
              fontWeight: 500,
            },
          }}
        />
      </Stack>
    </Box>
  );
}

export default CancelAppointmentModalBody;
